import { Link } from 'react-router-dom';
import { Heart, Mail, Shield, ArrowRight } from 'lucide-react';

interface FooterSafetyProps {
  onStartFree?: () => void;
}

export function FooterSafety({ onStartFree }: FooterSafetyProps) {
  return (
    <footer className="bg-slate-900 text-white">
      {onStartFree && (
        <div className="border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Start building the life you want
            </h2>
            <p className="text-lg text-slate-400 mb-8 max-w-xl mx-auto">
              Free forever. No credit card. Takes 30 seconds to get your first win.
            </p>
            <button
              onClick={onStartFree}
              className="group inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold text-lg rounded-full hover:bg-slate-100 transition-all duration-200 shadow-lg"
            >
              Get started free
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div className="lg:col-span-2">
            <div className="font-heading text-2xl font-bold mb-4">VZMO</div>
            <p className="text-slate-400 mb-6 max-w-md leading-relaxed">
              Your AI life coach. Journal your thoughts, get personalized guidance, and track your progress toward the life you want.
            </p>

            <div className="flex items-start gap-4 p-5 bg-slate-800/50 rounded-2xl border border-slate-700/50">
              <div className="w-10 h-10 rounded-full bg-coral-500/20 flex items-center justify-center flex-shrink-0">
                <Heart className="h-5 w-5 text-coral-400" />
              </div>
              <div>
                <p className="text-slate-300 text-sm mb-2">
                  This is coaching, not therapy. If you're in crisis, please reach out:
                </p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <a href="tel:988" className="text-coral-400 hover:text-coral-300 transition-colors font-medium">
                    US: 988
                  </a>
                  <a href="tel:116123" className="text-coral-400 hover:text-coral-300 transition-colors font-medium">
                    UK: 116 123
                  </a>
                  <a href="tel:131114" className="text-coral-400 hover:text-coral-300 transition-colors font-medium">
                    AU: 13 11 14
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-5 text-white">Product</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/features" className="text-slate-400 hover:text-white transition-colors">
                  Features
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-slate-400 hover:text-white transition-colors">
                  Pricing
                </Link>
              </li>
              <li>
                <a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors">
                  How it works
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-5 text-white">Company</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/about" className="text-slate-400 hover:text-white transition-colors">
                  About
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-slate-400 hover:text-white transition-colors">
                  Privacy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-slate-400 hover:text-white transition-colors">
                  Terms
                </Link>
              </li>
              <li>
                <a href="mailto:team@vzmo.ai" className="text-slate-400 hover:text-white transition-colors inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Contact
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">
            {new Date().getFullYear()} VZMO. Made with care.
          </p>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Shield className="h-4 w-4" />
            <span>Your data is encrypted and private</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
